/**
 * DC «Новый год» (29.10.2026, владелец): «Пусть туда попадают все, у кого в модалке выставлен флаг "!". Так же, туда вносим
 * лиды, скрины переписок и историю клиента». Вторая вкладка ДК (dcTabs.tsx).
 *   • Карточка = клиент (телефон): брони и лиды со слотом с «!!!» на игры сезона (1 дек … 15 янв) + НГ-заявки без времени
 *     (сайты «Сайт НГ…» и ручные «+ Заявка»). Данные — GET /api/ny/cards (только офис); поиск и фильтр — на клиенте.
 *   • Тап по брони — как «БЕЗ предоплаты»: сетка дня брони (ночной слот — D-1) + модалка; после закрытия — снова сюда
 *     (DcGridScreen), поиск, фильтр и раскрытая карточка на месте (nyKeep).
 *   • Опроса нет: список свежий при входе во вкладку и после своих правок.
 */
import { useEffect, useMemo, useState } from "react";
import { filterCards, filterCounts, fmtDM, groupCards, NY_FILTERS, todayOrenIso, type NyBooking, type NyFilter } from "./nyModel";
import { nyKeep, useNyCards } from "./useNy";
import { NyCardView } from "./NyCardView";
import { NyLeadForm } from "./NyLeadForm";
import css from "./dcSections.module.css";
import ny from "./dcNy.module.css";

export function DcNySection({ onOpen, onToast }: { onOpen: (b: NyBooking) => void; onToast: (m: string) => void }) {
  const { data, loading, error, reload } = useNyCards();
  const [query, setQuery] = useState(nyKeep.query);
  const [filter, setFilter] = useState<NyFilter>(nyKeep.filter);
  const [open, setOpen] = useState<string | null>(nyKeep.open);
  /* карточку, раскрытую до ухода в сетку, по возврату прокручиваем к себе — один раз */
  const [scrollKey] = useState<string | null>(nyKeep.open);
  const [adding, setAdding] = useState(false);
  useEffect(() => { nyKeep.query = query; nyKeep.filter = filter; nyKeep.open = open; }, [query, filter, open]);

  const today = todayOrenIso();
  const cards = useMemo(() => data?.cards ?? [], [data]);
  const counts = useMemo(() => filterCounts(cards, query), [cards, query]);
  const groups = useMemo(() => groupCards(filterCards(cards, query, filter), today), [cards, query, filter, today]);

  const added = (key: string) => {
    setAdding(false); setQuery(""); setFilter("all"); setOpen(key);
    onToast("Заявка добавлена"); reload();
  };

  return (
    <div className={css.main}>
      <div className={ny.top}>
        <div className={css.search}>
          <svg className={css.lens} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" aria-hidden="true"><circle cx="6" cy="6" r="4" /><path d="M9 9l4 4" /></svg>
          <input type="text" className={css.searchInput} placeholder="Имя или телефон…" value={query} autoComplete="off"
            onChange={(e) => setQuery(e.target.value)} aria-label="Поиск по имени или телефону" />
          {query ? (
            <button type="button" className={css.searchClr} aria-label="Очистить" onClick={() => setQuery("")}>
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" /></svg>
            </button>
          ) : null}
        </div>
        <button type="button" className={css.btnAdd} onClick={() => setAdding((a) => !a)} aria-expanded={adding}>
          <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M5 1v8M1 5h8" /></svg>
          Заявка
        </button>
      </div>
      {adding ? <NyLeadForm onDone={added} onCancel={() => setAdding(false)} /> : null}

      <div className={ny.chips} role="tablist" aria-label="Фильтр карточек">
        {NY_FILTERS.map((f) => (
          <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
            className={`${ny.chip} ${filter === f.key ? ny.chipOn : ""}`} onClick={() => setFilter(f.key)}>
            {f.label} <b>{counts[f.key]}</b>
          </button>
        ))}
      </div>
      {data?.season ? (
        <div className={ny.season}>Сезон {fmtDM(data.season.from)} — {fmtDM(data.season.to)}: брони и лиды с «!!!» на эти даты и новогодние заявки.</div>
      ) : null}
      {error && data ? <div className={ny.warn}>Список не обновился: {error}</div> : null}

      {error && !data ? (
        <div className={css.err}>Ошибка загрузки: {error} <button type="button" className={ny.retry} onClick={reload}>Повторить</button></div>
      ) : loading && !data ? (
        <div className={css.loading}>Загрузка…</div>
      ) : cards.length === 0 ? (
        <div className={ny.emptyText}>
          Пока пусто. Сюда попадают <b>брони и лиды со слотом с «!!!»</b> на игры с 1 декабря по 15 января и <b>новогодние заявки</b> —
          с сайтов и внесённые вручную. Заявку без времени — кнопкой «+ Заявка», флаг «!!!» — в модалке брони.
        </div>
      ) : groups.length === 0 ? (
        <div className={ny.emptyText}>Ничего не нашлось — поменяйте поиск или фильтр.</div>
      ) : (
        groups.map((g) => (
          <section key={g.group} aria-label={g.title}>
            <div className={ny.group}>{g.title} <b>{g.cards.length}</b></div>
            {g.cards.map((c) => (
              <NyCardView key={c.key} card={c} today={today} open={open === c.key} scrollTo={scrollKey === c.key && open === c.key}
                onToggle={() => setOpen((o) => (o === c.key ? null : c.key))} onOpenBooking={onOpen} onChanged={reload} onToast={onToast} />
            ))}
          </section>
        ))
      )}
    </div>
  );
}
